import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SpaceProps } from 'styled-system';
import { AspectRatioBox, ScreenshotImage } from 'views/Summary/imageComponents';
import styled from 'styled-components';
import Body from 'ui/Typography/Body';
import { useIntl } from 'react-intl';
import {
  Product,
  AssemblyInstructionsComponent,
  AssemblyInstructionMeasurementPosition,
  AssemblyInstructionMeasurementPoint,
  AssemblyInstructionEntityPoint
} from '@inter-ikea-storageone/range';
import screenshotSettings from 'config/screenshotSettings';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import media from 'ui/media';
import { useScreenshot } from 'contexts/screenshot';
import LoadingSpinner from 'ui/LoadingSpinner/LoadingSpinner';
import Title from 'ui/Typography/Title';
import { useCore } from 'contexts/core';
import { useServiceSettings } from 'contexts/serviceSettings';
import MeasurementLine from './MeasurementLine';
import { getMarketProducts } from 'ducks/products';
import { ProductUtils } from '@inter-ikea-core/core';
import ProductIdentifier from '@ingka/product-identifier';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import { useTranslations } from 'hooks/useTranslations';

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 50%;
`;

const Container = styled.div`
  margin: ${({ theme }) => theme.space.l} 0 ${({ theme }) => theme.space.xl} 0;
  display: flex;
  ${media.mobile`
    display: block;
  `}
  @media print {
    page-break-inside: avoid;
  }
`;

const Wrapper = styled.div`
  @media print {
    page-break-inside: avoid;
  }
`;

const ImageContainer = styled.div`
  width: 80%;
  position: relative;
  ${media.mobile`
    width: 100%;
  `}
`;

const SvgContainer = styled.svg`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 2;
`;

const PrintTitle = styled(Title)`
  display: none;
  @media print {
    display: block;
  }
`;

const SvgText = styled.text`
  font-size: 1.5rem;
  font-weight: bold;
`;

type Props = SpaceProps & {
  productsInScene: Product[];
  shouldGenerate: boolean;
};

const ProductListContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 3rem;
`;

type ProductListProps = {
  entityPointsData: AssemblyInstructionEntityPoint[];
};

const ProductList = ({ entityPointsData }: ProductListProps) => {
  const products = useSelector(getMarketProducts);
  const productList = entityPointsData
    .sort((a, b) => a.instructionNbr - b.instructionNbr)
    .map(entityPoint => {
      const product = products.find(
        product => product.coreProductId === ProductUtils.productRef(entityPoint.entity)
      );
      return { product, instructionNbr: entityPoint.instructionNbr };
    });
  return (
    <ProductListContainer>
      {productList.map(productData => {
        const product = productData.product;
        const instructionNbr = productData.instructionNbr;
        if (product) {
          return (
            <div key={instructionNbr} style={{ display: 'flex' }}>
              <div style={{ flex: '0 0 20px' }}>
                <Title type={ThemeFontStyleTypeEnum.headingXSmall}>{`${instructionNbr}.`}</Title>
              </div>
              <div style={{ flex: '1' }}>
                <Title type={ThemeFontStyleTypeEnum.headingXSmall}>{`${product.name}`}</Title>
                <ProductIdentifier value={ProductUtility.formatItemNo(product.ruItemNo)} />
                <Body type={ThemeFontStyleTypeEnum.bodyMedium}>{product.typeName}</Body>
              </div>
            </div>
          );
        }
      })}
    </ProductListContainer>
  );
};

const AssemblyInstructionsKitchen = ({ shouldGenerate, ...props }: Props) => {
  const { theme } = useServiceSettings();
  const { useMetric } = useTranslations();
  const fontFamily = `${theme.fontFace.family}, ${theme.fontFace.fallbacks.join(',')}`;
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const { assemblyKitchenImageData, captureKitchenAssemblyInstructions } = useScreenshot();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      assemblyKitchenImageData.length == 0 &&
      shouldGenerate &&
      coreApi.entities.getByComponent(AssemblyInstructionsComponent.id).length > 0
    ) {
      setLoading(true);
      setTimeout(() => {
        captureKitchenAssemblyInstructions().then(() => setLoading(false));
      }, 200);
    }
  }, [
    assemblyKitchenImageData.length,
    captureKitchenAssemblyInstructions,
    coreApi.entities,
    shouldGenerate
  ]);

  return loading ? (
    <LoadingWrapper>
      <LoadingSpinner />
    </LoadingWrapper>
  ) : (
    <Wrapper {...props} data-testid="assembly-instructions-test-id">
      <PrintTitle type={ThemeFontStyleTypeEnum.headingLarge} mt="md">
        {formatMessage({ id: 'storageone.kitchenDrawingTitle' })}
      </PrintTitle>
      <Title type={ThemeFontStyleTypeEnum.headingSmall}>
        {formatMessage({ id: 'storageone.kitchenDrawingTitle' })}
      </Title>
      {useMetric ? (
        <Body type={ThemeFontStyleTypeEnum.bodySmall} mb="xxl">
          {formatMessage({ id: 'storageone.kitchenDrawingMeasurementInMMText' })}{' '}
          {formatMessage({ id: 'storageone.kitchenDrawingMeasurementLiabilityDisclaimer' })}
        </Body>
      ) : (
        <Body type={ThemeFontStyleTypeEnum.bodySmall} mb="xxl">
          {formatMessage({ id: 'storageone.kitchenDrawingMeasurementLiabilityDisclaimer' })}
        </Body>
      )}
      {assemblyKitchenImageData.length > 0 &&
        assemblyKitchenImageData.map((imageData, index) => (
          <Fragment key={`imageData-${index}`}>
            <Title type={ThemeFontStyleTypeEnum.headingSmall}>
              {formatMessage({ id: imageData.title })}
            </Title>
            <Container>
              <ImageContainer>
                <AspectRatioBox>
                  <SvgContainer
                    viewBox={`0 0 ${screenshotSettings.default.size.x} ${screenshotSettings.default.size.y}`}
                    fontFamily={fontFamily}
                  >
                    {imageData.entityPointsData.map(
                      (entityData: AssemblyInstructionEntityPoint) => {
                        return (
                          <Fragment key={entityData.entity.id}>
                            <circle
                              cx={entityData.coord.x}
                              cy={entityData.coord.y}
                              r={20}
                              fill="#ECECEC"
                            />
                            <SvgText
                              x={entityData.coord.x}
                              y={entityData.coord.y + 2}
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              {entityData.instructionNbr}
                            </SvgText>
                          </Fragment>
                        );
                      }
                    )}
                    {Object.keys(imageData.measurementPointsData).map(pos => {
                      const points = imageData.measurementPointsData[
                        pos as AssemblyInstructionMeasurementPosition
                      ] as AssemblyInstructionMeasurementPoint[];
                      return points.map((point, index) => {
                        if (index > 0) {
                          const measurement =
                            point.positionAlongWall - points[index - 1].positionAlongWall;

                          return (
                            measurement > 5 && (
                              <MeasurementLine
                                key={index}
                                startPoint={points[index - 1].coord}
                                endPoint={point.coord}
                                measurement={measurement}
                                position={pos as AssemblyInstructionMeasurementPosition}
                              />
                            )
                          );
                        }
                      });
                    })}
                  </SvgContainer>
                  {imageData.image && <ScreenshotImage alt={'noImage'} src={imageData.image} />}
                </AspectRatioBox>
              </ImageContainer>

              <ProductList entityPointsData={imageData.entityPointsData} />
            </Container>
          </Fragment>
        ))}
    </Wrapper>
  );
};

export default AssemblyInstructionsKitchen;
